import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import ShareBar from "@/pages/detail/components/ShareBar";
import RatingPanel from "@/pages/detail/components/RatingPanel";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatMeta, getItemById, getRelated } from "@/utils/catalog";

export default function Detail() {
  const { id } = useParams();
  const item = getItemById(id);

  usePageMeta({
    title: item ? `${item.title} - ${item.genres.join("·")} - 可可影视` : "作品未找到 - 可可影视",
    description: item
      ? `${item.title}（${item.year}，${item.genres.join("、")}）：${item.summary} 导演 ${item.director}，主演 ${item.actors.join("、")}，评分 ${item.rating}。`
      : "抱歉，未找到该影视作品，返回可可影视首页继续浏览。",
    keywords: item
      ? `${item.title},${item.genres.join(",")},${item.region}影视,影视评分,可可影视`
      : "可可影视",
    canonical: item ? `https://hrxxdk.cn/detail/${item.id}` : undefined,
  });

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="mt-3 text-sm text-foreground-500">作品可能已下线，去看看其他精彩内容吧。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              返回首页
            </Link>
            <Link
              to="/movies"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览电影
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelated(item, 6);
  const categoryPath = item.category === "movie" ? "/movies" : "/tvseries";
  const categoryLabel = item.category === "movie" ? "电影" : "电视剧";

  const metaRows = [
    { label: "类型", value: item.genres.join(" / ") },
    { label: "导演", value: item.director },
    { label: "主要演员", value: item.actors.join("、") },
    { label: "地区", value: item.region },
    { label: item.category === "movie" ? "上映日期" : "首播日期", value: item.releaseDate },
    { label: item.category === "movie" ? "片长" : "集数", value: formatMeta(item) },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="relative w-full overflow-hidden">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} ${item.genres.join(" ")}`}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/95 via-background-950/70 to-background-950/40"></div>
          <div className="relative w-full px-4 py-10 md:px-8 md:py-14">
            <Breadcrumb
              tone="dark"
              items={[
                { label: "首页", to: "/" },
                { label: categoryLabel, to: categoryPath },
                { label: item.title },
              ]}
            />

            <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end">
              <div className="w-36 shrink-0 overflow-hidden rounded-lg border border-background-50/20 md:w-48" style={{ aspectRatio: "2 / 3" }}>
                <img
                  src={item.poster}
                  alt={`${item.title} 海报`}
                  title={`${item.title} 影视作品封面`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                    {categoryLabel}
                  </span>
                  {item.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-100/90"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                  {item.title}
                </h1>
                <p className="mt-2 text-sm text-background-100/85">
                  {item.year} · {item.region} · {item.genres.join(" · ")} · {formatMeta(item)}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <span className="flex items-center gap-1 text-lg font-bold text-accent-400">
                    <i className="ri-star-fill"></i>
                    {item.rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-background-100/80">导演 {item.director}</span>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <ShareBar title={item.title} summary={item.summary} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="flex flex-col gap-8 lg:col-span-2">
              <section>
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-file-text-line text-primary-600"></i>
                  剧情概要
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {item.synopsis}
                </p>
              </section>

              <section>
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-information-line text-primary-600"></i>
                  作品信息
                </h2>
                <dl className="mt-4 overflow-hidden rounded-lg border border-background-200/80">
                  {metaRows.map((row, index) => (
                    <div
                      key={row.label}
                      className={`flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center ${
                        index % 2 === 0 ? "bg-background-50" : "bg-background-100"
                      }`}
                    >
                      <dt className="w-28 shrink-0 text-sm font-medium text-foreground-500">{row.label}</dt>
                      <dd className="text-sm text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section>
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-price-tag-3-line text-primary-600"></i>
                  所属分类
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={categoryPath}
                    className="whitespace-nowrap rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-200"
                  >
                    {categoryLabel}分类
                  </Link>
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={categoryPath}
                      className="whitespace-nowrap rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </section>
            </div>

            <div className="flex flex-col gap-6">
              <RatingPanel rating={item.rating} />
              <div className="rounded-lg border border-background-200/80 bg-background-100 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">主创阵容</h2>
                <div className="mt-4 flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-foreground-500">导演</span>
                    <span className="font-medium text-foreground-900">{item.director}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-foreground-500">主演</span>
                    <div className="flex flex-wrap gap-2">
                      {item.actors.map((actor) => (
                        <span
                          key={actor}
                          className="rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-700"
                        >
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="mb-6 flex items-end justify-between gap-4 md:mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
                看过这部，还推荐
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                相关推荐
              </h2>
            </div>
            <Link
              to={categoryPath}
              className="hidden items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-accent-600 md:flex"
            >
              更多{categoryLabel}
              <i className="ri-arrow-right-s-line text-lg"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {related.map((relatedItem) => (
              <MovieCard key={relatedItem.id} item={relatedItem} showSummary={false} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}